import { Box, Button } from '@mui/material'
import { useContext } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'

import { NumberStatus, NumberStatusSize } from '../../Components'
import { AppSettingsContext, AuctionCountContext, AuctionCountLoader, useMinter, useUpdateMinter } from '../../Contexts'
import { AuctionsToolbar } from '../../Toolbar'
import WorldBasePage from '../WorldBasePage'

interface StatusProps {
  autoWidth?: boolean
  size?: NumberStatusSize
}

const AvailableAuctionsStatus: React.FC<StatusProps> = (props) => {
  const { availableAuctionCount } = useContext(AuctionCountContext)
  const { size, autoWidth } = props
  return <NumberStatus autoWidth={autoWidth} size={size} title="Available Auctions" to="/auctions/available" value={availableAuctionCount} />
}

const LiveAuctionsStatus: React.FC<StatusProps> = (props) => {
  const { liveAuctionCount } = useContext(AuctionCountContext)
  const { size, autoWidth } = props
  return <NumberStatus autoWidth={autoWidth} size={size} title="Live Auctions" to="/auctions/live" value={liveAuctionCount} />
}

const DutchAuctionsStatus: React.FC<StatusProps> = (props) => {
  const { dutchAuctionCount } = useContext(AuctionCountContext)
  const { size, autoWidth } = props
  return <NumberStatus autoWidth={autoWidth} size={size} title="Dutch Auctions" to="/auctions/dutch" value={dutchAuctionCount} />
}

const CompletedAuctionsStatus: React.FC<StatusProps> = (props) => {
  const { completedAuctionCount } = useContext(AuctionCountContext)
  const { size, autoWidth } = props
  return <NumberStatus autoWidth={autoWidth} size={size} title="Completed Auctions" to="/auctions/completed" value={completedAuctionCount} />
}

const UpcomingAuctionsStatus: React.FC<StatusProps> = (props) => {
  const { upcomingAuctionCount } = useContext(AuctionCountContext)
  const { size, autoWidth } = props
  return <NumberStatus autoWidth={autoWidth} size={size} title="Upcoming Auctions" to="/auctions/upcoming" value={upcomingAuctionCount} />
}

const MintedAuctionsStatus: React.FC<StatusProps> = (props) => {
  const { mintedAuctionCount } = useContext(AuctionCountContext)
  const { size, autoWidth } = props
  return <NumberStatus autoWidth={autoWidth} size={size} title="Minted Auctions" to="/auctions/minted" value={mintedAuctionCount} />
}

const UpdateButton: React.FC = () => {
  const { minter } = useMinter()
  const { refreshAuctionCounts, clearAuctionCounts } = useContext(AuctionCountContext)
  const updateMinter = useUpdateMinter()

  const onUpdate = async () => {
    if (minter) {
      clearAuctionCounts?.()
      await updateMinter?.()
      refreshAuctionCounts?.()
    }
  }

  return (
    <Box padding={2}>
      <Button onClick={onUpdate} variant="outlined">
        Update
      </Button>
    </Box>
  )
}

const HelmetLoader: React.FC = () => {
  const { availableAuctionCount } = useContext(AuctionCountContext)
  return (
    <Helmet>
      <meta content="https://app.world.xyo.network/auctions" property="og:url" />
      <meta content={`${availableAuctionCount} New Geotokens Now Available - Claim your piece of the world!`} property="og:description" />
      <meta content="https://app.world.xyo.network/img/xyo-world-meta.png" property="og:image" />
    </Helmet>
  )
}

const AuctionsPage: React.FC = () => {
  const { developerMode } = useContext(AppSettingsContext)
  return (
    <WorldBasePage toolbar={<AuctionsToolbar />}>
      <AuctionCountLoader>
        <HelmetLoader />
        <Box alignItems="center" display="flex" flexDirection="column" flexGrow={1} justifyContent="center">
          <Box sx={{ display: { md: 'flex', xs: 'none' } }} alignItems="center" display="flex" flexGrow={1} flexWrap="wrap" justifyContent="center">
            <Box padding={0.5}>
              <AvailableAuctionsStatus />
            </Box>
            <Box padding={0.5}>
              <LiveAuctionsStatus />
            </Box>
            <Box padding={0.5}>
              <DutchAuctionsStatus />
            </Box>
            <Box padding={0.5}>
              <CompletedAuctionsStatus />
            </Box>
            <Box padding={0.5}>
              <UpcomingAuctionsStatus />
            </Box>
            <Box padding={0.5}>
              <MintedAuctionsStatus />
            </Box>
          </Box>
          <Box
            sx={{ display: { md: 'none', xs: 'flex' } }}
            alignItems="center"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            margin={1}
            width="100vw"
          >
            <Box margin={1} width="100%">
              <AvailableAuctionsStatus autoWidth size={NumberStatusSize.Large} />
            </Box>
            <Box margin={1} width="100%">
              <LiveAuctionsStatus autoWidth size={NumberStatusSize.Large} />
            </Box>
            <Box padding={1} width="100%">
              <DutchAuctionsStatus autoWidth size={NumberStatusSize.Large} />
            </Box>
            <Box padding={1} width="100%">
              <CompletedAuctionsStatus autoWidth size={NumberStatusSize.Large} />
            </Box>
            <Box padding={1} width="100%">
              <UpcomingAuctionsStatus autoWidth size={NumberStatusSize.Large} />
            </Box>
            <Box padding={1} width="100%">
              <MintedAuctionsStatus autoWidth size={NumberStatusSize.Large} />
            </Box>
          </Box>
          <Box display="flex">
            <Box alignItems="center" display="flex" justifyContent="center">
              <Button component={Link} to="/map" variant="outlined">
                View Map
              </Button>
            </Box>
            <Box alignItems="center" display="flex" justifyContent="center">
              <UpdateButton />
            </Box>
            <Box alignItems="center" display={developerMode ? 'flex' : 'none'} justifyContent="center">
              <Button component={Link} to="/auctions/json" variant="outlined">
                Json
              </Button>
            </Box>
          </Box>
        </Box>
      </AuctionCountLoader>
    </WorldBasePage>
  )
}

export default AuctionsPage
